import DateInput from '../../components/DateInput';
import React, { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { Palette, Plus, X, Trash2, Edit2, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { videoEmbedUrl, looksLikeImageUrl, themeStatus } from '../../lib/media';

const BLANK = {
  name: '', hero_title: '', hero_tagline: '',
  hero_image_url: '', hero_video_url: '', music_url: '',
  accent_color: '#003D80', background_color: '',
  start_date: '', end_date: '', enabled: false,
};

export default function AdminThemes() {
  const [themes, setThemes] = useState([]);
  const [edit, setEdit] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = () => api.get('/themes').then(r => setThemes(r.data));
  useEffect(() => { load(); }, []);

  const remove = async () => {
    await api.delete(`/themes/${confirmDel.theme_id}`);
    setConfirmDel(null);
    load();
  };
  const toggle = async (t) => {
    await api.put(`/themes/${t.theme_id}`, { ...t, enabled: !t.enabled }); load();
  };

  return (
    <div className="p-6 md:p-8 max-w-6xl">
      <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-accent font-medium mb-2">LANDING</div>
          <h1 className="text-3xl md:text-4xl font-heading font-bold tracking-tight">Seasonal themes</h1>
          <p className="text-fg-muted mt-2">Design hero image, video and music for holidays / seasons. Only one enabled-and-in-window theme applies.</p>
        </div>
        <button onClick={()=>setEdit({...BLANK})} className="btn-primary text-sm inline-flex items-center gap-2" data-testid="new-theme-btn">
          <Plus className="w-4 h-4"/> New theme
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {themes.map(t => (
          <div key={t.theme_id} className="surface border border-soft rounded-xl overflow-hidden" data-testid={`theme-card-${t.theme_id}`}>
            {t.hero_image_url && <div className="h-32 bg-soft"><img src={t.hero_image_url} alt="" className="w-full h-full object-cover"/></div>}
            <div className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-heading font-semibold flex items-center gap-2">
                    <Palette className="w-4 h-4 text-brand"/> {t.name}
                  </h3>
                  <p className="text-xs text-fg-muted mt-1">{t.start_date || '—'} → {t.end_date || '—'}</p>
                </div>
                <button onClick={()=>toggle(t)} className={`text-xs inline-flex items-center gap-1 ${t.enabled ? 'text-brand' : 'text-fg-muted'}`} data-testid={`toggle-theme-${t.theme_id}`}>
                  {t.enabled ? <Eye className="w-3.5 h-3.5"/> : <EyeOff className="w-3.5 h-3.5"/>}
                  {t.enabled ? 'Enabled' : 'Off'}
                </button>
              </div>
              {(() => {
                const st = themeStatus(t);
                const cls = st.key === 'live' ? 'bg-emerald-100 text-emerald-800'
                          : st.key === 'expired' ? 'bg-[#B83A3A]/10 text-[#B83A3A]'
                          : st.key === 'scheduled' ? 'bg-[#FFF4D6] text-[#8A5A00]'
                          : 'bg-soft text-fg-muted';
                return (
                  <span className={`inline-block mt-2 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${cls}`} data-testid={`theme-status-${t.theme_id}`}>
                    {st.label}
                  </span>
                );
              })()}
              {themeStatus(t).key === 'expired' && (
                <div className="text-[11px] text-[#B83A3A] mt-1.5 flex items-start gap-1" data-testid={`theme-expired-warning-${t.theme_id}`}>
                  <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0"/>
                  This theme is enabled but its end date passed — visitors won't see it. Extend the dates to show it again.
                </div>
              )}
              <p className="text-sm text-fg-muted mt-2 italic">{t.hero_tagline || '—'}</p>
              {/* Live color swatches — accent + background */}
              <div className="mt-3 flex items-center gap-3 text-[11px] text-fg-muted">
                <div className="flex items-center gap-1.5" data-testid={`theme-swatch-accent-${t.theme_id}`}>
                  <span className="w-5 h-5 rounded-full border-2 border-white shadow-sm" style={{ background: t.accent_color || '#003D80' }}/>
                  <span className="font-mono">{(t.accent_color || '#003D80').toUpperCase()}</span>
                </div>
                <div className="flex items-center gap-1.5" data-testid={`theme-swatch-bg-${t.theme_id}`}>
                  <span className="w-5 h-5 rounded-full border-2 border-white shadow-sm" style={{ background: t.background_color || '#F5EFE0' }}/>
                  <span className="font-mono">{(t.background_color || '#F5EFE0').toUpperCase()}</span>
                </div>
              </div>
              <div className="mt-3 flex gap-2">
                <button onClick={()=>setEdit(t)} className="text-brand p-1" data-testid={`edit-theme-${t.theme_id}`}><Edit2 className="w-4 h-4"/></button>
                <button onClick={()=>setConfirmDel(t)} className="text-[#B83A3A] p-1" data-testid={`del-theme-${t.theme_id}`}><Trash2 className="w-4 h-4"/></button>
              </div>
            </div>
          </div>
        ))}
        {!themes.length && <div className="col-span-full text-center text-fg-muted py-8">No themes yet.</div>}
      </div>

      {edit && <ThemeModal theme={edit} onClose={()=>setEdit(null)} onSaved={()=>{ load(); setEdit(null); }}/>}

      {confirmDel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={()=>setConfirmDel(null)} data-testid="delete-theme-modal">
          <div className="surface border border-soft rounded-xl shadow-xl w-full max-w-sm p-5" onClick={e=>e.stopPropagation()}>
            <h2 className="font-heading font-semibold mb-2">Delete theme {confirmDel.name}?</h2>
            <p className="text-sm text-fg-muted">This removes the theme permanently.</p>
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={()=>setConfirmDel(null)} className="btn-secondary text-sm" data-testid="delete-theme-cancel">Cancel</button>
              <button onClick={remove} className="text-sm rounded-md px-4 py-2 bg-[#B83A3A] hover:bg-[#8F2C2C] text-white" data-testid="delete-theme-confirm">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ThemeModal({ theme, onClose, onSaved }) {
  const isNew = !theme.theme_id;
  const [form, setForm] = useState(theme);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      if (isNew) await api.post('/themes', form);
      else await api.put(`/themes/${theme.theme_id}`, form);
      onSaved();
    } catch (e) { alert('Save failed: ' + (e?.response?.data?.detail || e.message)); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose} data-testid="theme-modal">
      <div className="surface border border-soft rounded-xl shadow-xl w-full max-w-5xl max-h-[90vh] overflow-y-auto" onClick={e=>e.stopPropagation()}>
        <div className="p-4 border-b border-soft flex items-center justify-between">
          <h2 className="font-heading font-semibold">{isNew ? 'New seasonal theme' : `Edit · ${theme.name}`}</h2>
          <button onClick={onClose} className="text-fg-muted hover:text-fg"><X className="w-5 h-5"/></button>
        </div>

        <div className="grid md:grid-cols-2 gap-0 max-h-[75vh] overflow-hidden">
          {/* Left — edit form */}
          <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-3 overflow-y-auto border-r border-soft">
            <Field label="Name *" full><input className="input" value={form.name} onChange={e=>set('name', e.target.value)} data-testid="th-name"/></Field>
            <Field label="Hero title"><input className="input" value={form.hero_title||''} onChange={e=>set('hero_title', e.target.value)} data-testid="th-title"/></Field>
            <Field label="Hero tagline"><input className="input" value={form.hero_tagline||''} onChange={e=>set('hero_tagline', e.target.value)} data-testid="th-tagline"/></Field>
            <Field label="Hero image" full>
              <div className="flex items-center gap-2 mb-1.5">
                <label className="btn-secondary text-xs cursor-pointer inline-flex items-center gap-1.5" data-testid="th-image-upload-btn">
                  Upload image
                  <input type="file" accept="image/*" className="hidden" data-testid="th-image-upload-input"
                         onChange={e => {
                           const f = e.target.files?.[0]; if (!f) return;
                           if (f.size > 1024 * 1024 * 2) { alert('Please keep the image under 2 MB.'); return; }
                           const reader = new FileReader();
                           reader.onload = ev => set('hero_image_url', ev.target.result);
                           reader.readAsDataURL(f);
                         }}/>
                </label>
                {form.hero_image_url && form.hero_image_url.startsWith('data:') && (
                  <span className="text-[11px] text-emerald-700">✓ Uploaded image in use</span>
                )}
                {form.hero_image_url && (
                  <button type="button" onClick={() => set('hero_image_url', '')} className="text-[11px] text-[#B83A3A]" data-testid="th-image-clear">Remove</button>
                )}
              </div>
              <input className="input" value={form.hero_image_url?.startsWith('data:') ? '' : (form.hero_image_url || '')}
                     placeholder={form.hero_image_url?.startsWith('data:') ? '(uploaded image in use — paste a URL to replace)' : '…or paste a direct image URL (.jpg / .png / .webp)'}
                     onChange={e=>set('hero_image_url', e.target.value)}/>
              {!looksLikeImageUrl(form.hero_image_url) && !form.hero_image_url?.startsWith('data:') && (
                <div className="text-[11px] text-[#B83A3A] mt-1" data-testid="th-image-warning">
                  ⚠ This doesn't look like a direct image link. Use "Upload image" above, or a URL ending in .jpg / .png / .webp
                  (right-click an image → "Copy image address"). Webpage links won't display.
                </div>
              )}
            </Field>
            <Field label="Hero video URL">
              <input className="input" value={form.hero_video_url||''} onChange={e=>set('hero_video_url', e.target.value)} placeholder="YouTube / Vimeo link or direct .mp4"/>
              {form.hero_video_url && videoEmbedUrl(form.hero_video_url) && (
                <div className="text-[11px] text-emerald-700 mt-1" data-testid="th-video-ok">
                  ✓ YouTube/Vimeo detected — check the preview on the right. If it says
                  "Video unavailable", the owner has disabled embedding — pick a different video.
                </div>
              )}
            </Field>
            <Field label="Music URL">
              <input className="input" value={form.music_url||''} onChange={e=>set('music_url', e.target.value)} placeholder="Direct .mp3 / .ogg link"/>
            </Field>
            <Field label="Accent color"><input className="input h-10" type="color" value={form.accent_color || '#003D80'} onChange={e=>set('accent_color', e.target.value)}/></Field>
            <Field label="Background color"><input className="input h-10" type="color" value={form.background_color || '#F5EFE0'} onChange={e=>set('background_color', e.target.value)}/></Field>
            <Field label="Start date"><DateInput value={form.start_date||''} onChange={e=>set('start_date', e.target.value)}/></Field>
            <Field label="End date"><DateInput value={form.end_date||''} onChange={e=>set('end_date', e.target.value)}/></Field>
            <label className="sm:col-span-2 flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!form.enabled} onChange={e=>set('enabled', e.target.checked)} data-testid="th-enabled"/>
              Enable this theme (only the most recently-created enabled-and-in-window theme is shown)
            </label>
          </div>

          {/* Right — live preview */}
          <ThemePreview form={form}/>
        </div>

        <div className="px-5 py-4 flex justify-end gap-2 border-t border-soft">
          <button onClick={onClose} className="btn-secondary text-sm">Cancel</button>
          <button onClick={save} disabled={saving || !form.name} className="btn-primary text-sm" data-testid="th-save">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ThemePreview({ form }) {
  const bg = form.background_color || '#F5EFE0';
  const accent = form.accent_color || '#003D80';
  // Pick a readable text color based on background luminance.
  const luminance = (c) => {
    const m = c.match(/^#?([\da-f]{2})([\da-f]{2})([\da-f]{2})$/i);
    if (!m) return 0.7;
    const [r, g, b] = [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)];
    return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  };
  const textColor = luminance(bg) > 0.55 ? '#0F1B2D' : '#FAF6EE';
  return (
    <div className="p-5 overflow-y-auto" data-testid="theme-preview">
      <div className="text-xs uppercase tracking-wider text-fg-muted mb-3">Live preview</div>
      <div
        className="rounded-2xl overflow-hidden border border-soft shadow-sm relative"
        style={{ background: bg, color: textColor }}
      >
        {/* Hero video / image / fallback — mirrors what the landing page renders. */}
        <div className="aspect-[16/9] w-full relative overflow-hidden bg-soft">
          {form.hero_video_url && videoEmbedUrl(form.hero_video_url) ? (
            <iframe src={videoEmbedUrl(form.hero_video_url)} title="video preview"
                    className="w-full h-full" frameBorder="0"
                    allow="autoplay; encrypted-media" data-testid="theme-preview-video"/>
          ) : form.hero_video_url ? (
            <video src={form.hero_video_url} autoPlay muted loop playsInline className="w-full h-full object-cover"/>
          ) : form.hero_image_url ? (
            <img src={form.hero_image_url} alt="preview hero" className="w-full h-full object-cover"
                 onError={e => { e.currentTarget.style.opacity = '0.4'; }}/>
          ) : (
            <div className="w-full h-full flex items-center justify-center text-fg-muted/60 text-sm">
              (no hero image or video)
            </div>
          )}
          {/* Subtle gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent pointer-events-none"/>
        </div>

        {/* Hero text */}
        <div className="p-6">
          <div className="text-[10px] uppercase tracking-[0.2em] font-semibold" style={{ color: accent }}>
            PHYSIOGENIC · SEASONAL
          </div>
          <h3 className="font-heading font-bold text-2xl md:text-3xl mt-2 leading-tight">
            {form.hero_title || 'Your hero headline goes here.'}
          </h3>
          {form.hero_tagline && <p className="mt-2 text-sm opacity-80">{form.hero_tagline}</p>}
          <div className="mt-4 flex gap-2">
            <button
              type="button"
              className="px-4 py-2 rounded-md text-sm font-semibold"
              style={{ background: accent, color: '#FFFFFF' }}
            >
              Book a session
            </button>
            <button
              type="button"
              className="px-4 py-2 rounded-md text-sm font-medium border"
              style={{ borderColor: accent, color: accent }}
            >
              Learn more
            </button>
          </div>
        </div>
      </div>

      {/* Accent / background swatches */}
      <div className="mt-4 flex items-center gap-4 text-xs text-fg-muted">
        <span className="flex items-center gap-1.5">
          <span className="w-4 h-4 rounded border border-soft" style={{ background: accent }}/> accent
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-4 h-4 rounded border border-soft" style={{ background: bg }}/> background
        </span>
        {form.start_date && form.end_date && (
          <span className="ml-auto">{form.start_date} → {form.end_date}</span>
        )}
      </div>

      {/* Music preview if URL provided */}
      {form.music_url && (
        <div className="mt-4">
          <div className="text-xs text-fg-muted mb-1">Music preview</div>
          <audio controls src={form.music_url} className="w-full h-8" data-testid="theme-preview-audio">
            Your browser does not support audio playback.
          </audio>
        </div>
      )}

      <p className="text-[11px] text-fg-muted mt-4">
        Hint: this preview updates live as you type. Use a Pexels / Unsplash URL for the hero image.
      </p>
    </div>
  );
}

function Field({ label, children, full }) {
  return <label className={full ? 'md:col-span-2 block' : 'block'}>
    <div className="text-xs text-fg-muted mb-1">{label}</div>{children}
  </label>;
}
